import React, { useState, useEffect } from 'react'
import { Clock, Calendar as CalendarIcon } from 'lucide-react'
import { getVacationEventsByMonth, TECH_DEPARTMENT_BUDGET } from '@/lib/real-data-service'
import { BudgetMetrics } from './BudgetMetrics'

interface VacationData {
  name: string
  department: string
  role: string
  daysRemaining: number
  daysUsed: number
  daysAvailable: number
}

interface DepartmentMetricsProps {
  hierarchyVacationData: VacationData[]
  formatCurrency: (amount: number) => string
}

export function DepartmentMetrics({ hierarchyVacationData, formatCurrency }: DepartmentMetricsProps) {
  const [vacationEvents, setVacationEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load vacation events asynchronously
  useEffect(() => {
    const loadVacationEvents = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get current month vacation events
        const currentMonth = new Date().getMonth() + 1;
        const events = await getVacationEventsByMonth(2025, currentMonth);
        setVacationEvents(events);
      } catch (err) {
        console.error('Error loading vacation events:', err);
        // Don't show error to user, just show empty state
        setError(null);
        setVacationEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Temporarily disable vacation events loading to prevent dashboard crashes
    // TODO: Fix vacation API call to include required parameters
    setIsLoading(false);
    setVacationEvents([]);
    // loadVacationEvents();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Custo da Área */}
      <BudgetMetrics 
        budgetData={TECH_DEPARTMENT_BUDGET}
        formatCurrency={formatCurrency}
      />

      {/* Matriz Saldo de Férias */}
      <div className="card animate-fade-in-up">
        <h3 className="font-display text-white font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          <div className="p-2 rounded-monumenta bg-brand-primary/10">
            <Clock className="w-5 h-5 text-brand-primary" />
          </div>
          Saldo de Férias
        </h3>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {hierarchyVacationData.length > 0 ? hierarchyVacationData.map((vacation, index) => {
            const isLowBalance = vacation.daysRemaining < 15
            return (
            <div key={index} className={`card-interactive group flex justify-between items-center p-4 transition-all duration-200 ${isLowBalance ? 'border-warning/30 bg-warning/5' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-display font-medium truncate group-hover:text-brand-primary transition-colors duration-200">{vacation.name}</p>
                <p className="text-xs text-brand-gray-light font-body">{vacation.department} • {vacation.role}</p>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className={`text-sm font-display font-bold ${isLowBalance ? 'text-warning' : 'text-white'} group-hover:scale-105 transition-transform duration-200`}>
                  {vacation.daysRemaining} dias
                </p>
                <p className="text-xs text-brand-gray font-body">{vacation.daysUsed}/{vacation.daysAvailable}</p>
              </div>
            </div>
          )
          }) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-brand-gray mx-auto mb-3" />
              <p className="text-brand-gray-light text-sm font-body">Nenhum funcionário encontrado na hierarquia</p>
            </div>
          )}
        </div>
      </div>

      {/* Calendário Férias Equipe */}
      <div className="card animate-fade-in-up">
        <h3 className="font-display text-white font-bold mb-6 flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          <div className="p-2 rounded-monumenta bg-brand-primary/10">
            <CalendarIcon className="w-5 h-5 text-brand-primary" />
          </div>
          Calendário Férias - {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
        </h3>
        
        {/* Current Month Events */}
        <div className="mb-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-brand-gray-light text-sm font-body">Carregando eventos...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-red-500 text-xl"></span>
              </div>
              <p className="text-red-400 text-sm font-body">{error}</p>
            </div>
          ) : vacationEvents.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {vacationEvents.map((event) => (
                <div
                  key={event.id}
                  className={`p-4 rounded-monumenta border-l-4 transition-all duration-200 hover:shadow-monumenta-lg group ${
                    event.type === 'scheduled' ? 'border-l-brand-blue bg-brand-blue/10 hover:border-brand-blue/50' :
                    event.type === 'leave' ? 'border-l-warning bg-warning/10 hover:border-warning/50' :
                    'border-l-success bg-success/10 hover:border-success/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-3 h-3 rounded-full animate-pulse ${
                          event.type === 'scheduled' ? 'bg-brand-blue' :
                          event.type === 'leave' ? 'bg-warning' :
                          'bg-success'
                        }`}></div>
                        <h4 className="text-sm font-display font-bold text-white group-hover:text-brand-primary transition-colors duration-200">{event.employeeName}</h4>
                        <span className="text-xs text-brand-gray-light font-body">({event.department})</span>
                      </div>
                      <p className="text-xs text-brand-gray-light font-body mb-2 leading-relaxed">{event.description}</p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-brand-gray font-body bg-brand-dark/30 px-2 py-1 rounded-monumenta">
                          {new Date(event.startDate).toLocaleDateString('pt-BR')} - {new Date(event.endDate).toLocaleDateString('pt-BR')}
                        </span>
                        <span className="text-brand-gray font-body bg-brand-dark/30 px-2 py-1 rounded-monumenta">
                          {event.days} dia{event.days > 1 ? 's' : ''}
                        </span>
                        <span className={`px-2 py-1 rounded-monumenta text-xs font-display font-medium ${
                          event.status === 'approved' 
                            ? 'bg-success/20 text-success border border-success/30' 
                            : 'bg-warning/20 text-warning border border-warning/30'
                        }`}>
                          {event.status === 'approved' ? 'Aprovado' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="w-16 h-16 text-brand-gray mx-auto mb-4" />
              <p className="text-brand-gray-light text-sm font-body">Nenhuma féria/licença este mês</p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-gray-700/50 space-y-3">
          <div className="flex items-center gap-3 p-2 rounded-monumenta hover:bg-brand-dark/30 transition-colors duration-200">
            <div className="w-4 h-4 rounded-full bg-brand-blue shadow-monumenta"></div>
            <span className="text-xs text-brand-gray-light font-body">
              Férias programadas ({isLoading ? '...' : vacationEvents.filter(e => e.type === 'scheduled').length})
            </span>
          </div>
          <div className="flex items-center gap-3 p-2 rounded-monumenta hover:bg-brand-dark/30 transition-colors duration-200">
            <div className="w-4 h-4 rounded-full bg-warning shadow-monumenta"></div>
            <span className="text-xs text-brand-gray-light font-body">
              Licenças ({isLoading ? '...' : vacationEvents.filter(e => e.type === 'leave').length})
            </span>
          </div>
          <div className="flex items-center gap-3 p-2 rounded-monumenta hover:bg-brand-dark/30 transition-colors duration-200">
            <div className="w-4 h-4 rounded-full bg-success shadow-monumenta"></div>
            <span className="text-xs text-brand-gray-light font-body">
              Folgas aprovadas ({isLoading ? '...' : vacationEvents.filter(e => e.type === 'timeoff').length})
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}